import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  ArchivedStatus, 
  DocumentContainer, 
  DocumentContent, 
  DocumentFooter, 
  DocumentHeader, 
  DocumentHeaderLeft, 
  DocumentHeaderRight, 
  Title, 
  Value 
} from './VaultDocument.styled';
import { Icon } from '../../../../../elements';
import MenuButtonWrapper from '../../../../../elements/v2/MenuButtonWrapper';
import RenameVaultModal from '../RenameVaultModal/RenameVaultModal';
import ArchiveVaultModal from '../ArchiveVaultModal/ArchiveVaultModal';
import ShareVaultModal from '../ShareVaultModal/ShareVaultModal';
import { useStore } from '../../../../../hooks';

const VaultDocument = ({ status, name, description, latest_updated, created_by, stored, id, files_stored }) => {
  const { authStore } = useStore();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [creatorName, setCreatorName] = useState('');

  useEffect(() => {
    const getCreatorName = async () => {
      try {
        if (!authStore.users.length) {
          await authStore.fetchAllUsers();
        }
        
        const creator = authStore.users.find(user => user.id === created_by);
        if (creator) {
          setCreatorName(`${creator.first_name} ${creator.last_name}`);
        } else {
          setCreatorName(created_by);
        }
      } catch (error) {
        setCreatorName(created_by);
      }
    };

    getCreatorName();
  }, [authStore, created_by]);

  const handleContainerClick = (e) => {
    if (showShareModal || showRenameModal || showArchiveModal) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    if (e.target.closest('.menu-wrapper')) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    const currentMode = searchParams.get('mode');
    const modeParam = currentMode ? `&mode=${currentMode}` : '';
    navigate(`/v2/vault/documents?id=${id}&customerId=${authStore.selectedAccount?.id}&vaultName=${encodeURIComponent(name)}${modeParam}`);
  };

  const handleRename = () => {
    setShowRenameModal(false);
  };

  const handleArchive = () => {
    setShowArchiveModal(false);
  };

  const handleCloseShareModal = () => {
    setShowShareModal(false);
  };

  const menuItems = [
    { 
      label: 'Rename Vault', 
      onClick: () => {
        setShowRenameModal(true);
      }
    },
    { 
      label: 'Manage Access', 
      onClick: () => {
        console.log('Manage Access clicked');
      }
    },
    {
      label: 'Share',
      onClick: () => {
        setShowShareModal(true);
      }
    },
    { 
      label: 'Archive Vault', 
      onClick: () => {
        setShowArchiveModal(true);
      }
    },
    { 
      label: 'View Activity Log', 
      onClick: () => {
        console.log('View Activity Log clicked');
      }
    }
  ];

  return (
    <>
      <DocumentContainer 
        status={status} 
        onClick={handleContainerClick} 
        style={{ cursor: 'pointer' }}
        data-testid="document-container"
      >
        <DocumentHeader>
          <DocumentHeaderLeft>{name}</DocumentHeaderLeft>
          <DocumentHeaderRight>
            {status === 'archived' && (
              <ArchivedStatus>
                <Icon name="group" />
                Archived
              </ArchivedStatus>
            )}
            <div 
              className="menu-wrapper" 
              onClick={(e) => {
                e?.preventDefault();
                e?.stopPropagation();
              }}
            >
              <MenuButtonWrapper items={menuItems} />
            </div>
          </DocumentHeaderRight>
        </DocumentHeader> 
        <DocumentContent>
          {description}
        </DocumentContent>
        <DocumentFooter>
          <div>
            <Title>Created by: </Title>
            <Value>{creatorName}</Value>
          </div>
          <div>
            <Title>Files: </Title>
            <Value>{files_stored}</Value>
          </div>
        </DocumentFooter>
      </DocumentContainer>

      <RenameVaultModal
        isOpen={showRenameModal}
        onClose={() => setShowRenameModal(false)}
        onSubmit={handleRename}
        currentName={name}
        description={description}
        fileId={id}
        created_by={authStore.userId}
        files_stored={files_stored}
      />

      <ArchiveVaultModal
        isOpen={showArchiveModal}
        onClose={() => setShowArchiveModal(false)}
        onSubmit={handleArchive}
        currentName={name}
        description={description}
        fileId={id}
        created_by={authStore.userId}
        files_stored={files_stored}
      />

      <ShareVaultModal
        isOpen={showShareModal}
        onClose={handleCloseShareModal}
        vaultId={id}
        vaultName={name}
      />
    </>
  );
};

export default VaultDocument; 